import { useMemo } from "react";
import { DAILY_REPORT_STATUS } from "constants/index";
import { GET_TEAM_DAILY_STANDUP_REPORT } from "graphql/queries";
import { useQueryData } from "hooks";

const keyName = "teamDailyReport";

/**
 * useTeamDailyReport
 * @param {string} teamId
 * @param {string} selectedDate
 */
export default function useTeamDailyReport({ teamId, selectedDate }) {
  const { loading, data, error } = useQueryData({
    queryName: GET_TEAM_DAILY_STANDUP_REPORT,
    variables: {
      teamId,
      filters: { selectedDate },
    },
    skip: !teamId,
    keyName,
  });

  const groupedData = useMemo(() => {
    if (!data?.[keyName]?.participantReports) return {};

    const groupedReportsByDate = groupReportsByDate(data[keyName].participantReports);

    return {
      ...data[keyName],
      participantReports: sortedReports(groupedReportsByDate),
      participationMetrics: getParticipationMetrics(groupedReportsByDate),
    };
  }, [data]);

  return {
    loading,
    data: groupedData,
    error,
  };
}

/**
 * Get participation metrics from the date grouped reports
 * @param {*} reports
 *
 * @returns {Object} - Metrics object with total, completed, and percentage
 */
const getParticipationMetrics = (reports = {}) =>
  Object.keys(reports).reduce((metricsAcc, date) => {
    if (!metricsAcc[date]) metricsAcc[date] = {};

    metricsAcc[date].total = reports[date]?.length || 0;
    metricsAcc[date].completed =
      reports[date]?.filter((entry) => DAILY_REPORT_STATUS[entry.status] === DAILY_REPORT_STATUS.COMPLETED).length || 0;

    if (metricsAcc[date].total > 0) {
      metricsAcc[date].percentage = Math.round((metricsAcc[date].completed / metricsAcc[date].total) * 100);
    } else {
      metricsAcc[date].percentage = 0;
    }

    return metricsAcc;
  }, {});

/**
 * Group reports by date
 * @param {Array} reports - Array of report objects
 *
 * @returns {Object} - Object with date as keys and array of reports as values
 */
const groupReportsByDate = (reports = []) =>
  reports.reduce((acc, report) => {
    let newReport = { ...report };
    const date = report.date;

    if (!acc[date]) {
      acc[date] = [];
    }

    newReport.standupEntries = report.standupEntries.reduce((entriesAcc, entry) => {
      entriesAcc[entry.question] = entry;

      return entriesAcc;
    }, {});

    if (Object.keys(newReport.standupEntries).length > 0) {
      acc[date].push(newReport);
    }

    return acc;
  }, {});

/**
 * Sort reports by participant name
 * @param {Object} reports - Object with date as keys and array of reports as values
 *
 * @return {Array} - Sorted array of reports
 **/
const sortedReports = (reports = {}) =>
  Object.fromEntries(
    Object.entries(reports).map(([date, reportList]) => [
      date,
      [...reportList].sort((a, b) => a?.user?.name?.localeCompare(b?.user?.name)),
    ])
  );
